<template>
    <div class="frame-card">
        <div class="frame-card__header">
            <div class="frame-card__title">
                Экран #{{ index + 1 }}
            </div>
            <div class="frame-card__id" :title="'ID: ' + prettyId(frame)">
                {{ prettyId(frame) }}
            </div>
        </div>

        <div class="frame-card__content">
            <div class="frame-card__row">
                <span class="frame-card__label">Повторений:</span>
                <span class="frame-card__value">{{ safeNumber(frame.repetitions) }} в день</span>
            </div>
            <div class="frame-card__row cost-row">
                <span class="frame-card__label">Стоимость:</span>
                <div class="cost-display">
                    <span class="cost-amount">{{ formatMoney(frame.cost) }}</span>
                    <span v-if="frame.costBreakdown && frame.costBreakdown.length" class="cost-slots">
                        {{ frame.costBreakdown.length }} слотов
                    </span>
                </div>
            </div>
            <div class="frame-card__row">
                <span class="frame-card__label">Требуемая длительность креатива:</span>
                <span class="frame-card__value">{{ formatSeconds(frame.duration) }} сек</span>
            </div>

            <!-- Периоды -->
            <div v-if="(frame.periods || []).length" class="periods-section">
                <div class="section-label">Периоды</div>
                <div class="periods-list">
                    <div v-for="(p, pi) in frame.periods || []" :key="pi" class="period-item"
                        :title="formatPeriod(p.start_time, p.end_time)">
                        <span class="period-date">{{ formatPeriod(p.start_time, p.end_time) }}</span>
                        <span class="period-days">({{ calculatePeriodDays(p.start_time, p.end_time) }})</span>
                    </div>
                </div>
            </div>

            <!-- Временные настройки -->
            <div v-if="groupedTimeSettings.length" class="time-settings-section">
                <div class="section-label">Время показа</div>
                <div class="time-settings-list">
                    <div v-for="(dayGroup, di) in groupedTimeSettings" :key="di" class="time-setting-item">
                        <span class="day-name">{{ dayName(dayGroup.day) }}</span>
                        <div class="time-setting-item__ranges">
                            <span v-for="(t, ti) in dayGroup.times" :key="ti" class="time-range">
                                {{ msToHM(t.start_time || t.startTime) }} — {{ msToHM(t.end_time || t.endTime) }} ({{
                                    calculateTimeHours(t.start_time || t.startTime, t.end_time || t.endTime) }})
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { formatDateOnlyInRtbTZ } from '../../../utils/timezones';

export default {
    name: 'FrameCard',
    props: {
        frame: {
            type: Object,
            required: true
        },
        index: {
            type: Number,
            required: true
        }
    },
    computed: {
        // Инициализированные настройки времени, отсортированные по дням недели
        initializedTimeSettings() {
            const timeSettings = this.initializeTimeSettings(this.frame.time_settings || this.frame.timeSettings || []);
            // Сортируем по дням недели: понедельник (1) - воскресенье (7)
            return [...timeSettings].sort((a, b) => {
                const dayA = Number(a.day) || 0;
                const dayB = Number(b.day) || 0;
                return dayA - dayB;
            });
        },
        // Группированные настройки времени по дням недели
        groupedTimeSettings() {
            const timeSettings = this.initializedTimeSettings;
            const grouped = {};

            // Группируем по дням недели
            timeSettings.forEach(t => {
                const day = Number(t.day) || 0;
                if (!grouped[day]) {
                    grouped[day] = {
                        day: day,
                        times: []
                    };
                }
                grouped[day].times.push(t);
            });

            // Преобразуем объект в массив и сортируем по дням
            return Object.values(grouped).sort((a, b) => a.day - b.day);
        }
    },
    methods: {
        prettyId(frameData) {
            // frameData содержит объект frame внутри себя
            const frame = frameData.frame || frameData;
            return frame.gid || frame.external_id || frame.id || 'N/A';
        },
        safeNumber(n) {
            return n != null ? Number(n) : 0;
        },
        formatMoney(amount) {
            if (amount == null) return new Intl.NumberFormat('ru-RU', {
                style: 'currency',
                currency: 'KZT',
                currencyDisplay: 'symbol',
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
            }).format(0);
            return new Intl.NumberFormat('ru-RU', {
                style: 'currency',
                currency: 'KZT',
                currencyDisplay: 'symbol',
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
            }).format(Number(amount));
        },
        formatPeriod(start, end) {
            if (!start && !end) return 'Не указан';
            const s = start ? formatDateOnlyInRtbTZ(start) : 'Начало';
            const e = end ? formatDateOnlyInRtbTZ(end) : 'Конец';
            return `${s} – ${e}`;
        },
        dayName(dayIndex) {
            const days = ['', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье'];
            return days[dayIndex] || `День ${dayIndex}`;
        },
        msToHM(ms) {
            const n = Number(ms);
            if (!Number.isFinite(n) || n < 0) return '00:00';
            if (n === 0) return '00:00';
            // Если время равно 24 часам (86400000 мс), показываем "24:00" вместо "00:00"
            if (n === 86400000) {
                return '24:00';
            }
            const totalSeconds = Math.floor(n / 1000);
            const hours = Math.floor(totalSeconds / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        },
        // Утилитарная функция для инициализации времени
        initializeTimeSettings(timeSettings) {
            if (!timeSettings || !Array.isArray(timeSettings)) return [];

            return timeSettings.map(t => {
                const startMs = t.start_time || t.startTime || 0;
                const endMs = t.end_time || t.endTime || 0;

                const startH = Math.floor(startMs / 3600000);
                const startM = Math.floor((startMs % 3600000) / 60000);
                const endH = Math.floor(endMs / 3600000);
                const endM = Math.floor((endMs % 3600000) / 60000);

                // Форматируем время начала
                let startTimeFormatted = `${startH.toString().padStart(2, '0')}:${startM.toString().padStart(2, '0')}`;
                if (startMs === 86400000) {
                    startTimeFormatted = '24:00';
                }

                // Форматируем время окончания
                let endTimeFormatted = `${endH.toString().padStart(2, '0')}:${endM.toString().padStart(2, '0')}`;
                if (endMs === 86400000) {
                    endTimeFormatted = '24:00';
                }

                return {
                    ...t,
                    start_time: startMs,
                    end_time: endMs,
                    startTime: startMs,
                    endTime: endMs,
                    startTimeFormatted: startTimeFormatted,
                    endTimeFormatted: endTimeFormatted
                };
            });
        },
        formatSeconds(ms) {
            const seconds = Math.floor(ms / 1000);
            return seconds.toLocaleString('ru-RU');
        },
        formatHours(ms) {
            if (!ms) return '0ч';
            const hours = Math.floor(ms / (1000 * 60 * 60));
            return `${hours}ч`;
        },
        getHoursWord(hours) {
            if (hours % 10 === 1 && hours % 100 !== 11) return 'час';
            if ([2, 3, 4].includes(hours % 10) && ![12, 13, 14].includes(hours % 100)) return 'часа';
            return 'часов';
        },
        calculateDaysFromPeriods(periods) {
            if (!periods || !Array.isArray(periods) || periods.length === 0) {
                return 1; // Минимум 1 день для избежания деления на ноль
            }

            let totalDays = 0;
            periods.forEach((period) => {
                if (!period.start_time || !period.end_time) return;

                const startDate = new Date(period.start_time);
                const endDate = new Date(period.end_time);

                // +1 чтобы включить оба дня (начало и конец)
                const days = Math.max(1, Math.floor((endDate - startDate) / (24 * 60 * 60 * 1000)) + 1);
                totalDays += days;
            });

            return Math.max(1, totalDays); // Минимум 1 день для избежания деления на ноль
        },
        calculatePeriodDays(startTime, endTime) {
            if (!startTime || !endTime) return '0 дней';

            const startDate = new Date(startTime);
            const endDate = new Date(endTime);
            const days = Math.max(1, Math.floor((endDate - startDate) / (24 * 60 * 60 * 1000)) + 1);

            return `${days} ${this.getDaysWord(days)}`;
        },
        getDaysWord(days) {
            if (days % 10 === 1 && days % 100 !== 11) return 'день';
            if ([2, 3, 4].includes(days % 10) && ![12, 13, 14].includes(days % 100)) return 'дня';
            return 'дней';
        },
        calculateTimeHours(startTime, endTime) {
            // Проверяем только на null/undefined, но не на 0
            if (startTime === null || startTime === undefined || endTime === null || endTime === undefined) {
                return '0ч';
            }

            const startMs = typeof startTime === 'number' ? startTime : new Date(startTime).getTime();
            const endMs = typeof endTime === 'number' ? endTime : new Date(endTime).getTime();

            if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return '0ч';

            // Если время представлено как миллисекунды от начала дня (0-86400000)
            // то нужно правильно вычислить разность
            let totalMs;
            if (endMs >= startMs) {
                // Обычный случай: время в пределах одного дня
                totalMs = endMs - startMs;
            } else {
                // Время переходит через полночь (например, 23:00 - 01:00)
                // В контексте времени дня это означает, что endTime меньше startTime
                // Но в нашем случае это скорее всего ошибка данных, считаем как есть
                totalMs = endMs - startMs;
            }

            const totalMinutes = Math.floor(totalMs / (1000 * 60));
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;

            if (hours === 0 && minutes === 0) return '0ч';
            if (hours === 0) return `${minutes}мин`;
            if (minutes === 0) return `${hours}ч`;

            return `${hours}ч ${minutes}мин`;
        },
        getMinutesWord(minutes) {
            if (minutes % 10 === 1 && minutes % 100 !== 11) return 'минута';
            if ([2, 3, 4].includes(minutes % 10) && ![12, 13, 14].includes(minutes % 100)) return 'минуты';
            return 'минут';
        },
    }
};
</script>

<style scoped>
.frame-card {
    background: var(--bg);
    border: 1px solid var(--card-border);
    border-radius: calc(var(--radius) - 4px);
    padding: 12px;
    transition: all 0.2s ease;

    &:hover {
        border-color: #cbd5e1;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
    }
}

.frame-card__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
    padding-bottom: 6px;
    border-bottom: 0.5px solid #d0d5dd;
}

.frame-card__title {
    font-weight: 600;
    color: var(--accent);
    font-size: 12px;
}

.frame-card__id {
    font-size: 10px;
    color: var(--muted);
    font-family: monospace;
}

.frame-card__content {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.frame-card__row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 11px;
}

.frame-card__label {
    color: var(--muted);
    font-weight: 400;
}

.frame-card__value {
    color: var(--accent);
    font-weight: 600;
}

.cost-display {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
}

.cost-amount {
    font-size: 12px;
    font-weight: 700;
    color: var(--accent);
    line-height: 1.2;
}

.cost-slots {
    font-size: 9px;
    color: var(--muted);
    font-weight: 500;
    background: rgba(15, 23, 42, 0.08);
    padding: 1px 4px;
    border-radius: 3px;
    opacity: 0.8;
}

.periods-section,
.time-settings-section {
    margin-top: 8px;
    padding-top: 6px;
    border-top: 1px solid rgba(15, 23, 42, 0.08);
}

.section-label {
    font-size: 10px;
    font-weight: 600;
    color: var(--accent);
    margin-bottom: 4px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.periods-list {
    display: flex;
    flex-direction: column;
    gap: 3px;
}

.period-item {
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.08), rgba(147, 197, 253, 0.05));
    border: 1px solid rgba(59, 130, 246, 0.15);
    padding: 4px 8px;
    border-radius: 6px;
    transition: all 0.2s ease;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.period-item:hover {
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.12), rgba(147, 197, 253, 0.08));
    border-color: rgba(59, 130, 246, 0.25);
}

.period-date {
    font-size: 10px;
    color: var(--accent);
    font-weight: 500;
}

.period-days {
    font-size: 9px;
    color: var(--muted);
    font-weight: 400;
    opacity: 0.8;
}

.time-settings-list {
    display: flex;
    flex-direction: column;
    gap: 3px;
}

.time-setting-item {
    background: linear-gradient(135deg, rgba(34, 197, 94, 0.08), rgba(134, 239, 172, 0.05));
    border: 1px solid rgba(34, 197, 94, 0.15);
    padding: 4px 8px;
    border-radius: 6px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    transition: all 0.2s ease;
}

.time-setting-item:hover {
    background: linear-gradient(135deg, rgba(34, 197, 94, 0.12), rgba(134, 239, 172, 0.08));
    border-color: rgba(34, 197, 94, 0.25);
}

.day-name {
    font-size: 10px;
    color: var(--accent);
    font-weight: 600;
    align-self: center;
    flex-shrink: 0;
    margin-right: 12px;
}

.time-setting-item__ranges {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    align-items: flex-end;
}

.time-range {
    font-size: 10px;
    color: var(--muted);
    font-family: monospace;
    font-weight: 500;
}

.cost-breakdown-info {
    font-size: 11px;
    color: #6b7280;
    font-weight: 400;
}
</style>
